'use client'

import { ComponentPropsWithoutRef, FC } from 'react'
import { Check, Minus } from 'lucide-react'
import { Button, cn } from 'design-system'

interface CheckboxProps
  extends Omit<
    ComponentPropsWithoutRef<typeof Button>,
    'onChange' | 'onClick' | 'children'
  > {
  checked: boolean
  onChange?: (checked: boolean) => void
  indeterminate?: boolean
  ariaLabel?: string
}

export const Checkbox: FC<CheckboxProps> = ({
  checked,
  onChange,
  indeterminate,
  ariaLabel,
  className,
  disabled,
  ...props
}) => {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!checked)
    }
  }

  const isIndeterminate = indeterminate && !checked
  const ariaChecked = isIndeterminate ? 'mixed' : checked

  return (
    <Button
      type="button"
      role="checkbox"
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-checked={ariaChecked}
      buttonType="transparent"
      buttonShape="square"
      className={cn(
        '!h-4 !w-4 !items-center !justify-center !rounded !border !border-solid !p-0 !text-base !font-normal transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300',
        {
          '!border-slate-300 !bg-transparent': !checked && !isIndeterminate,
          '!border-blue-600 !bg-blue-600': checked || isIndeterminate,
          '!cursor-not-allowed !opacity-50': disabled
        },
        className
      )}
      {...props}>
      {checked && <Check className="h-4 w-4 text-white" aria-hidden="true" />}
      {isIndeterminate && (
        <Minus className="h-4 w-4 text-white" aria-hidden="true" />
      )}
    </Button>
  )
}
